import { format } from "date-fns";
import { ChevronDownIcon } from "lucide-react";
import { type ComponentProps, useState } from "react";
import { Button } from "@/components/ui/atoms/button";
import { Calendar } from "@/components/ui/atoms/calendar";
import { Input } from "@/components/ui/atoms/input";
import { Label } from "@/components/ui/atoms/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/atoms/popover";

export type DateTimePickerProps = {
  value?: Date;
  onChange?: (date: Date | undefined) => void;
  className?: string;
} & Omit<ComponentProps<typeof Calendar>, "selected" | "onSelect" | "mode">;

export function DateTimePicker({
  value,
  onChange,
  className,
  ...calendarProps
}: DateTimePickerProps) {
  const [open, setOpen] = useState(false);
  const timeValue = value ? format(value, "HH:mm:ss") : "";

  return (
    <div className={className}>
      <div className="flex gap-4">
        <div className="flex flex-col gap-3">
          <Label className="px-1" htmlFor="date-picker">
            Date:{" "}
          </Label>
          <Popover onOpenChange={setOpen} open={open}>
            <PopoverTrigger
              render={
                <Button
                  className="w-32 justify-between font-normal"
                  id="date-picker"
                  size="lg"
                  variant="outline"
                />
              }
            >
              <span>{value ? value.toLocaleDateString() : "Select date"}</span>
              <ChevronDownIcon />
            </PopoverTrigger>
            <PopoverContent
              align="start"
              className="w-auto overflow-hidden p-0"
            >
              <Calendar
                captionLayout="dropdown"
                mode="single"
                onSelect={(date) => {
                  onChange?.(date);
                  setOpen(false);
                }}
                selected={value}
                {...calendarProps}
              />
            </PopoverContent>
          </Popover>
        </div>
        <div className="flex flex-col gap-3">
          <Label className="px-1" htmlFor="time-picker">
            Time:{" "}
          </Label>
          <Input
            className="appearance-none bg-background [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-calendar-picker-indicator]:appearance-none"
            id="time-picker"
            onChange={(e) => {
              const time = e.target.value;
              if (!(time && value)) {
                return;
              }
              const [hours, minutes, seconds] = time.split(":").map(Number);
              const newDate = new Date(value);
              newDate.setHours(hours, minutes, seconds || 0);
              onChange?.(newDate);
            }}
            step="1"
            type="time"
            value={timeValue}
          />
        </div>
      </div>
    </div>
  );
}

DateTimePicker.displayName = "DateTimePicker";
